import { ArcFilter, FilterExpression, PerspectiveFilter, RelationshipFilter } from 'types/filters';
import { isFilterVariable } from '@center-platform/spheres/filters';

/**
 * Converts a system name into a human-readable display name.
 * Strips the `center_` prefix, replaces underscores with spaces, and title-cases each word.
 */
function formatSystemName(systemName: string): string {
  return systemName
    .replace(/^center_/, '')
    .replaceAll('_', ' ')
    .replaceAll(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Converts a `FilterExpression` into a human-readable text string.
 *
 * Handles all canonical operators from `types/filters.d.ts` with special formatting
 * for common operators (`equals`, `in`, `not_equals`, `contains`) and a safe fallback
 * for any other operator.
 *
 * When `property` is `'type'` or `'label'`, values are resolved via the optional
 * `resolvedTypes` map; otherwise `formatSystemName()` is used as a fallback.
 *
 * @param filter - The filter expression to render
 * @param resolvedTypes - Optional map of system names to display metadata for value resolution
 * @returns Human-readable string representation of the filter
 */
export function renderFilterExpression(
  filter: FilterExpression,
  resolvedTypes?: Map<string, { displayName: string; avatar?: string }>
): string {
  const { property, operator, value, values } = filter;

  const resolveValue = (v: unknown): string => {
    const str = String(v ?? '');
    if ((property === 'type' || property === 'label') && resolvedTypes?.has(str)) {
      return resolvedTypes.get(str)!.displayName;
    }
    return formatSystemName(str);
  };

  switch (operator) {
    case 'equals':
      return `${property} = ${resolveValue(value)}`;
    case 'in':
      return `${property} in [${(values ?? []).map(resolveValue).join(', ')}]`;
    case 'not_equals':
      return `${property} \u2260 ${resolveValue(value)}`;
    case 'not_in':
      return `${property} not in [${(values ?? []).map(resolveValue).join(', ')}]`;
    case 'contains':
      return `${property} contains "${value ?? ''}"`;
    case 'starts_with':
      return `${property} starts with "${value ?? ''}"`;
    case 'ends_with':
      return `${property} ends with "${value ?? ''}"`;
    case 'greater_than':
      return `${property} > ${value}`;
    case 'less_than':
      return `${property} < ${value}`;
    case 'greater_than_equals':
      return `${property} >= ${value}`;
    case 'less_than_equals':
      return `${property} <= ${value}`;
    case 'between':
      return `${property} between ${filter.min} and ${filter.max}`;
    case 'exists':
      return `${property} exists`;
    case 'not_exists':
      return `${property} does not exist`;
    case 'is_true':
      return `${property} is true`;
    case 'is_false':
      return `${property} is false`;
    case 'is_empty':
      return `${property} is empty`;
    case 'is_not_empty':
      return `${property} is not empty`;
    case 'not_contains':
      return `${property} does not contain "${value ?? ''}"`;
    case 'contains_all':
      return `${property} contains all [${(values ?? []).map(resolveValue).join(', ')}]`;
    case 'within_last_days':
      return `${property} in the last ${value} days`;
    case 'within_next_days':
      return `${property} in the next ${value} days`;
    case 'last_7_days':
      return `${property} in the last 7 days`;
    case 'last_30_days':
      return `${property} in the last 30 days`;
    case 'date_within_days':
      return `${property} within ${value} days`;
    default:
      return `${property} ${operator} ${value ?? ''}`;
  }
}

/**
 * Converts a `RelationshipFilter` into human-readable text (e.g. "Duplicate of: Project Apollo").
 * A sibling to `renderFilterExpression`, not a fork of it — `RelationshipFilter` isn't a
 * `FilterExpression` (no `property`/`value`; it carries `relationshipSystemName`/`relatedTo`
 * with direction and minCount semantics FilterExpression doesn't have).
 *
 * `@`-tokens (e.g. `@me`) in `relatedTo` render literally — never resolved here.
 *
 * @param filter - The relationship filter to render
 * @param relationshipLabel - Display label for the relationship (resolved by the caller from its option list)
 * @param resolveSphereName - Resolves a sphere id to a display name; unresolved ids fall back to the raw id
 */
export function renderRelationshipFilter(
  filter: RelationshipFilter,
  relationshipLabel: string,
  resolveSphereName?: (sphereId: string) => string | undefined
): string {
  const { operator, relatedTo, direction } = filter;

  const DIRECTION_SUFFIXES: Record<string, string> = {
    source: ' (points to)',
    target: ' (pointed at by)',
  };
  const directionSuffix = (direction && DIRECTION_SUFFIXES[direction]) || '';

  if (operator === 'not_in') {
    if (relatedTo.length === 0) return `${relationshipLabel}: none${directionSuffix}`;
    const names = relatedTo.map((id) => (isFilterVariable(id) ? id : resolveSphereName?.(id) ?? id));
    return `${relationshipLabel}: not [${names.join(', ')}]${directionSuffix}`;
  }

  if (relatedTo.length === 0) return `${relationshipLabel}: any${directionSuffix}`;
  const names = relatedTo.map((id) => (isFilterVariable(id) ? id : resolveSphereName?.(id) ?? id));
  return `${relationshipLabel}: ${names.join(', ')}${directionSuffix}`;
}

/**
 * Converts a `PerspectiveFilter` into human-readable text (e.g.
 * "Responsible: alice, bob (at least 2)"). Sibling to
 * `renderRelationshipFilter` — a `PerspectiveFilter` isn't a `FilterExpression`
 * (it carries a perspective plus correlated assignee/minCount narrowing), so it
 * needs its own renderer rather than falling through `String(value)`, which
 * prints `[object Object]`.
 *
 * `@`-tokens (e.g. `@me`) in `assignees` render literally — never resolved here.
 *
 * @param filter - The perspective filter to render
 * @param perspectiveLabel - Display label for the perspective (resolved by the caller from its option list)
 * @param resolveSphereName - Resolves an assignee id to a display name; unresolved ids fall back to the raw id
 */
export function renderPerspectiveFilter(
  filter: PerspectiveFilter,
  perspectiveLabel: string,
  resolveSphereName?: (sphereId: string) => string | undefined
): string {
  const { operator, assignees, minCount } = filter;
  const names = (assignees ?? []).map((id) =>
    isFilterVariable(id) ? id : resolveSphereName?.(id) ?? id
  );

  if (operator === 'not_in') {
    // `minCount` is ignored by both engines for `not_in` (the condition is
    // "zero matches"), so it must not appear here either.
    return names.length === 0
      ? `${perspectiveLabel}: none`
      : `${perspectiveLabel}: not [${names.join(', ')}]`;
  }

  // An empty assignee list is a legitimate existence check ("has anyone
  // responsible"), not an unfinished row.
  const scope = names.length === 0 ? 'any' : names.join(', ');
  const atLeast = minCount !== undefined && minCount > 1 ? ` (at least ${minCount})` : '';
  return `${perspectiveLabel}: ${scope}${atLeast}`;
}

/** Resolvers the caller supplies so this util stays free of catalog lookups + i18n. */
export interface ArcFilterRenderContext {
  /** Localized name for the row's arc selector (e.g. "Primary arc", "Onboarding arc"). */
  arcLabel: string;
  /** Resolves an opaque, template-specific stage id to its localized name. */
  resolveStageName?: (stageId: string) => string | undefined;
  /** Resolves an `ArcHealthStatus` / `ArcLifecycle` value to its localized label. */
  resolveEnumLabel?: (axis: 'health' | 'lifecycle', value: string) => string | undefined;
  /** Localized connectors, so this util emits no hardcoded user-facing English. */
  labels: {
    /** Reads as "no arc matches" — the whole-row negation (`not_in`). */
    none: string;
    /** Reads as "has a matching arc" — an empty predicate is an existence check. */
    exists: string;
    /**
     * The `not_in` connector for a row that DOES carry axes — "not [At Risk]".
     * Optional so existing callers keep compiling; falls back to the literal
     * `not`, which is what this function hardcoded before EAINT-793 R-19 made
     * it reachable from the two recipient-facing filter surfaces (until then it
     * only rendered inside the advanced editor's preview).
     */
    negated?: string;
  };
}

/**
 * Converts an `ArcFilter` into human-readable text (e.g. "Primary arc: At Risk, In review").
 * A sibling to `renderFilterExpression`, not a fork — `ArcFilter` isn't a `FilterExpression`
 * (it carries an arc selector plus correlated stage/health/lifecycle axes).
 *
 * The axes render comma-joined because they are ANDed on the SAME arc
 * (arc-filter-ui-brief.md §2) — the row is one arc's condition, not a set of
 * independent assertions.
 *
 * Stage ids are never shown raw: an unresolvable id falls back to its literal value
 * only so a row naming a retired template still reads as *something* rather than
 * vanishing (dropping it would misrepresent a context that still filters on it).
 */
export function renderArcFilter(filter: ArcFilter, ctx: ArcFilterRenderContext): string {
  const { arcLabel, resolveStageName, resolveEnumLabel, labels } = ctx;
  const parts: string[] = [];

  (filter.healthStatus ?? []).forEach((v) => parts.push(resolveEnumLabel?.('health', v) ?? v));
  (filter.stageId ?? []).forEach((id) => parts.push(resolveStageName?.(id) ?? id));
  (filter.lifecycle ?? []).forEach((v) => parts.push(resolveEnumLabel?.('lifecycle', v) ?? v));

  if (filter.operator === 'not_in') {
    return parts.length === 0
      ? `${arcLabel}: ${labels.none}`
      : `${arcLabel}: ${labels.negated ?? 'not'} [${parts.join(', ')}]`;
  }

  // An empty predicate is a legitimate existence check ("has an onboarding arc"),
  // not an unfinished row.
  return parts.length === 0 ? `${arcLabel}: ${labels.exists}` : `${arcLabel}: ${parts.join(', ')}`;
}
